import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MessageSquare, MapPin, Clock, CheckCircle2, ArrowUpRight, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TOPICS = [
  { id: "application", label: "About my application" },
  { id: "deal",        label: "About a specific deal" },
  { id: "payment",     label: "About a payment" },
  { id: "press",       label: "Press / partnerships" },
  { id: "other",       label: "Something else" },
];

// PUB-06 — Contact
export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", topic: "", message: "" });
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  if (done) {
    return (
      <div className="bg-hero-rich min-h-[70vh] grid place-items-center px-4 py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-faint pointer-events-none opacity-50" />
        <div className="relative max-w-md text-center">
          <div className="mx-auto size-14 rounded-full bg-brand-50 text-brand-700 grid place-items-center mb-7 ring-4 ring-brand-100/40">
            <CheckCircle2 className="size-7" strokeWidth={2.2} />
          </div>
          <div className="eyebrow text-brand-700 mb-2">Message received</div>
          <h1 className="font-brand text-display text-ink font-bold tracking-[-0.02em]">Thanks, {form.name.split(" ")[0] || "friend"}.</h1>
          <p className="mt-5 text-body text-ink-muted leading-relaxed">
            We&rsquo;ve logged your note and will respond by email at{" "}
            <span className="font-semibold text-ink">{form.email}</span> within one business day.
          </p>
          <Link to="/" className="mt-9 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-ink hover:bg-brand-700 text-white font-semibold text-body-sm transition-colors">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface">
      {/* Full-width hero — chip + H1 + paragraph span the page so the body
          columns below start at the same baseline, no more misalignment. */}
      <section className="relative overflow-hidden bg-hero-rich border-b border-line">
        <div className="absolute inset-0 bg-grid-faint pointer-events-none opacity-50" />
        <div className="relative max-w-site mx-auto px-6 lg:px-12 pt-16 pb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-50 border border-brand-100 text-caption text-brand-700 uppercase tracking-wider font-semibold mb-6">
            <Mail className="size-3.5" /> Get in touch
          </div>
          <h1 className="font-brand font-bold text-ink leading-[1.04] tracking-[-0.025em] text-[44px] sm:text-[60px] lg:text-[64px] max-w-3xl">
            Talk to a human.{" "}
            <span className="relative inline-block text-brand-700">
              No queue.
              <svg className="absolute -bottom-2 left-0 w-full" height="14" viewBox="0 0 220 14" fill="none" preserveAspectRatio="none">
                <path d="M2 8 Q 60 2, 110 7 T 218 6" stroke="#5EEAD4" strokeWidth="3" strokeLinecap="round" fill="none" />
              </svg>
            </span>
          </h1>
          <p className="mt-6 text-body text-ink-muted leading-relaxed max-w-2xl">
            We answer every email within one business day. Most days it&rsquo;s less than two hours.
          </p>
        </div>
      </section>

      {/* Two-column body — both columns start at exactly the same Y. */}
      <div className="max-w-site mx-auto px-6 lg:px-12 py-14 lg:py-16 grid lg:grid-cols-12 gap-10 lg:gap-14">
        <aside className="lg:col-span-5">
          <div className="eyebrow text-brand-700 mb-4">Ways to reach us</div>
          <ul className="space-y-px bg-line border border-line rounded-2xl overflow-hidden">
            <Channel icon={<Mail className="size-4" />} label="Email" value="support@gcbc.com" hint="Best for application or payment questions" />
            <Channel icon={<MessageSquare className="size-4" />} label="In-app messaging" value="Available after sign-in" hint="Faster for active members" />
            <Channel icon={<Clock className="size-4" />} label="Hours" value="Mon–Fri · 9 AM – 6 PM ET" hint="We monitor email outside hours but reply during business hours" />
            <Channel icon={<MapPin className="size-4" />} label="HQ" value="Burlington, Vermont" hint="Mailing address available on request" />
          </ul>
        </aside>

        <form onSubmit={(e) => { e.preventDefault(); setDone(true); window.scrollTo({ top: 0 }); }}
              data-testid="contact-form"
              className="lg:col-span-7 space-y-6">
          <div className="eyebrow text-brand-700 mb-2">Send us a note</div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Your name" required>
              <Input value={form.name} onChange={set("name")} required data-testid="contact-name" className="h-12 bg-surface" />
            </Field>
            <Field label="Email" required>
              <Input type="email" value={form.email} onChange={set("email")} required data-testid="contact-email" className="h-12 bg-surface" />
            </Field>
          </div>
          <Field label="Topic" required>
            <Select value={form.topic} onValueChange={set("topic")}>
              <SelectTrigger className="h-12 bg-surface"><SelectValue placeholder="Choose a topic" /></SelectTrigger>
              <SelectContent>
                {TOPICS.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Message" required>
            <Textarea rows={8} value={form.message} onChange={set("message")} required maxLength={2000}
                      placeholder="Tell us what's on your mind. The more specific you can be, the faster we can help."
                      data-testid="contact-message" className="bg-surface" />
            <div className="text-caption text-ink-hint text-right tabular-nums mt-1">{form.message.length} / 2000</div>
          </Field>
          <div className="flex justify-end pt-2">
            <button type="submit"
                    disabled={!form.name || !form.email || !form.topic || !form.message}
                    data-testid="contact-submit-btn"
                    className="group relative inline-flex items-center gap-3 h-12 pl-7 pr-3 rounded-full bg-brand-700 hover:bg-brand-800 disabled:bg-line disabled:text-ink-hint disabled:cursor-not-allowed text-white font-brand font-bold text-body-sm shadow-[0_8px_24px_rgba(15,118,110,0.32)] hover:shadow-[0_12px_32px_rgba(15,118,110,0.42)] hover:-translate-y-0.5 transition-all duration-300 disabled:shadow-none disabled:translate-y-0">
              <span>Send message</span>
              <span className="size-8 rounded-full bg-white/15 grid place-items-center transition-transform group-hover:rotate-45 group-hover:bg-white/25">
                <Send className="size-3.5" strokeWidth={2.4} />
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Channel({ icon, label, value, hint }) {
  return (
    <li className="bg-surface p-5 flex items-start gap-4">
      <div className="size-10 rounded-xl bg-brand-50 text-brand-700 grid place-items-center shrink-0 ring-1 ring-brand-100">{icon}</div>
      <div className="min-w-0">
        <div className="text-eyebrow uppercase tracking-wider text-ink-hint">{label}</div>
        <div className="text-body font-semibold text-ink mt-0.5">{value}</div>
        <div className="text-caption text-ink-subtle mt-1 leading-snug">{hint}</div>
      </div>
    </li>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-eyebrow uppercase text-ink-muted mb-1.5 block">
        {label}{required && <span className="text-brand-700 normal-case"> *</span>}
      </Label>
      {children}
    </div>
  );
}
